import { getIANATimezoneStringForAbbreviation, timeFormatterFromMilliseconds, titleize } from 'helpers/sf_utils';
import React from 'react'
import { Card, CardBody, Label, Row, Table, UncontrolledTooltip } from 'reactstrap';
import { phoneNumberFormatter } from 'pages/Admin/Users/UserComponents/UserList/Formatters';
import { lowerCase } from 'lodash';
import CallsBarGraph from './CallsBarGraph';
import { CUSTOMER_BAR_GRAPH_COLOR } from 'helpers/sf_lead_constants';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { REPORTING_TIMEZONE } from 'helpers/sf_constants';
import { WHITE_LABELED_PRODUCT_NAMES, AppConfiguration } from 'config';

const CallStats = ({ transcription_sample, totalCallDuration, AllParticipantNames, colors, call_data, topics }) => {

    const name = (call_data[0]?.lead_detail?.first_name || call_data[0]?.lead_detail?.last_name) ? (call_data[0]?.lead_detail?.first_name || "") + ' ' + (call_data[0]?.lead_detail?.last_name || "") : "--";
    const phoneNumber = phoneNumberFormatter(call_data[0]?.lead_detail?.phone);

    const callReceived = call_data[0]?.quote_call?.created_at || call_data[0]?.lead_detail?.created_at;

    const getUniqueTopics = () => {
        const uniqueTopics = [];
        topics.forEach(topic => {
            if (!uniqueTopics.includes(topic.topicName)) {
                uniqueTopics.push(topic.topicName);
            }
        });
        return uniqueTopics;
    }

    function getTotalDurationForTopic(key) {
        const totalDuration = topics.reduce((total, topic) => {
            if (topic.topicName === key) {
                return total + topic.EndOffsetMillis - topic.BeginOffsetMillis;
            }
            return total;
        }, 0);
        return timeFormatterFromMilliseconds(totalDuration);
    }

    function calculateParticipantStats(participants) {
        const participantStats = {};
        participants.forEach(participant => {
            const totalTime = transcription_sample.ConversationCharacteristics.TalkTime.DetailsByParticipant[participant.ParticipantRole].TotalTimeMillis;
            const totalWords = transcription_sample.Transcript.reduce((total, segment) => {
                return segment.ParticipantRole === participant.ParticipantRole ? total + segment.Content.split(' ').length : total;
            }, 0);
            const totalSegments = transcription_sample.Transcript.filter(segment => segment.ParticipantRole === participant.ParticipantRole).length;
            const longestSegment = transcription_sample.Transcript.reduce((longest, segment) => {
                if (segment.ParticipantRole === participant.ParticipantRole && segment.EndOffsetMillis - segment.BeginOffsetMillis > longest) {
                    return segment.EndOffsetMillis - segment.BeginOffsetMillis;
                }
                return longest;
            }, 0);
            const interruptions = transcription_sample.ConversationCharacteristics.Interruptions.InterruptionsByInterrupter[participant.ParticipantRole]?.length ?? 0;
            const sentimentCount = transcription_sample.ConversationCharacteristics.Sentiment.OverallSentiment
            const quarterlySentiment = transcription_sample.ConversationCharacteristics.Sentiment.SentimentByPeriod["QUARTER"][participant.ParticipantRole]

            participantStats[participant.ParticipantRole] = {
                totalTime: totalTime,
                totalWords: totalWords,
                totalSegments: totalSegments,
                longestSegment: longestSegment,
                interruptions: interruptions,
                sentimentCount: sentimentCount,
                talkSpeed: totalTime > 0 ? (totalWords / (totalTime / 1000 / 60)).toFixed(2) : 0,
                nonTalkTime: transcription_sample.ConversationCharacteristics.NonTalkTime.TotalTimeMillis,
                quarterlySentiment: quarterlySentiment,
            };
        });

        return participantStats;
    }

    const participants = transcription_sample.Participants;
    const participantStats = calculateParticipantStats(participants)
    const totalNonTalkTime = participants.reduce((total, participant) => {
        return total + participantStats[participant.ParticipantRole].nonTalkTime;
    }, 0);
    const conversationRatio = parseInt((totalCallDuration.total_call_duration_milliseconds - totalNonTalkTime) / totalCallDuration.total_call_duration_milliseconds * 100);

    return (
        <div>
            <Card>
                <CardBody className="py-0">
                    <Row id="call-transcription-table">
                        <div className="px-0">
                            <Table className="mb-0 text-black">
                                <tbody>
                                    <tr>
                                        <td className="h4 pb-0 font-weight-semibold text-black">
                                            <div>
                                                <div className="d-flex align-items-top">
                                                    <p>
                                                        {name}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Phone
                                                </Label>
                                                <p className="mb-0">{phoneNumber}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Call Received
                                                </Label>
                                                <p className="mb-0">{callReceived ? format(utcToZonedTime(callReceived, getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)), 'MM/dd/yyyy hh:mm aa') : "--"}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Total Call Duration
                                                </Label>
                                                <p className="mb-0">{timeFormatterFromMilliseconds(totalCallDuration.total_call_duration_milliseconds)}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Participants
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-participants" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-participants">
                                                    The people on the call
                                                </UncontrolledTooltip>
                                                {AllParticipantNames.map((participant, index) => {
                                                    return (
                                                        <div className='d-flex align-items-center'>
                                                            <div style={{ backgroundColor: `${AppConfiguration?.PRODUCT_NAME !== WHITE_LABELED_PRODUCT_NAMES.INSURANCE_IO && !index ? AppConfiguration?.PRODUCT_FAV_COLOR : colors[index]}`, borderRadius: '50%', width: '15px', height: '15px', color: 'white', textAlign: 'center' }} className='me-1 fs-10'> {participant[0]}</div>
                                                            <p className='mb-0'>{titleize(participant)}</p>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Metrics
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-topics" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-topics">
                                                    Areas of the call when a metric was discussed
                                                </UncontrolledTooltip>
                                                <div>
                                                    {getUniqueTopics().map((topic, index) => (
                                                        <p className='mb-1'>
                                                            <span style={{ backgroundColor: `${AppConfiguration?.PRODUCT_NAME !== WHITE_LABELED_PRODUCT_NAMES.INSURANCE_IO && !index ? AppConfiguration?.PRODUCT_FAV_COLOR : colors[index]}` }} className='text-white fs-11 rounded px-1 me-1'>
                                                                {topic}
                                                            </span>
                                                        </p>
                                                    )
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    {
                                        participants.map((participant, index) => {
                                            return (
                                                <tr>
                                                    <td>
                                                        <div>
                                                            <div className='d-flex align-items-center'>
                                                                <Label className="mb-0 font-weight-semibold">
                                                                    {titleize(participant.ParticipantRole)} Talk Time
                                                                </Label>
                                                                <i className='ms-1 bx bx-info-circle text-black bx-xs' id={`infotooltip-talk-time-${lowerCase(participant.ParticipantRole)}`} />
                                                            </div>
                                                            <UncontrolledTooltip placement="top" target={`infotooltip-talk-time-${lowerCase(participant.ParticipantRole)}`}>
                                                                Total time {lowerCase(participant.ParticipantRole)} spent speaking on the call
                                                            </UncontrolledTooltip>
                                                            <p className="mb-0">Total : {timeFormatterFromMilliseconds(participantStats[participant.ParticipantRole].totalTime)}</p>
                                                            <p className="mb-0">Longest Segment : {timeFormatterFromMilliseconds(participantStats[participant.ParticipantRole].longestSegment)}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    }
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Conversation Ratio
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-conversation-ratio" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-conversation-ratio">
                                                    The percentage each participant spent speaking on the call
                                                </UncontrolledTooltip>
                                                <p className="mb-0">{conversationRatio}%</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Non Talk Time
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-non-talk-time" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-non-talk-time">
                                                    Total time no participants were speaking
                                                </UncontrolledTooltip>
                                                <p className="mb-0">{timeFormatterFromMilliseconds(totalNonTalkTime)}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Talk Speed
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-talk-speed" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-talk-speed">
                                                    Words per minute each participant spoke
                                                </UncontrolledTooltip>
                                                {
                                                    participants.map((participant, index) => {
                                                        return (
                                                            <p className="mb-0">{titleize(participant.ParticipantRole)} : {parseInt(participantStats[participant.ParticipantRole].talkSpeed)} wpm</p>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Interruptions
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-interruptions" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-interruptions">
                                                    Times each participant interrupted the other
                                                </UncontrolledTooltip>
                                                {
                                                    participants.map((participant, index) => {
                                                        return (
                                                            <p className="mb-0">{titleize(participant.ParticipantRole)} : {participantStats[participant.ParticipantRole].interruptions}</p>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Overall Sentiment
                                                    </Label>
                                                    <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-overall-sentiment" />
                                                </div>
                                                <UncontrolledTooltip placement="top" target="infotooltip-overall-sentiment">
                                                    The attitude of each participant overall on the call
                                                </UncontrolledTooltip>
                                                {
                                                    participants.map((participant, index) => {
                                                        return (
                                                            <p className="mb-0">{titleize(participant.ParticipantRole)} : {participantStats[participant.ParticipantRole].sentimentCount[participant.ParticipantRole]}</p>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className='d-flex align-items-center'>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Quaterly Sentiment
                                                </Label>
                                                <i className='ms-1 bx bx-info-circle text-black bx-xs' id="infotooltip-quarterly-sentiment" />
                                            </div>
                                            <UncontrolledTooltip placement="top" target="infotooltip-quarterly-sentiment">
                                                The attitude of each participant during each quarter of the call
                                            </UncontrolledTooltip>
                                            {participants.map((participant, participantIndex) => {
                                                return (
                                                    <div className='mb-4 pb-2'>
                                                        {participantIndex == 0 ?
                                                            <div className='d-flex align-items-end'>
                                                                <div>
                                                                    {participantStats[participant?.ParticipantRole]?.quarterlySentiment?.map((sentiment, index) => {
                                                                        return (
                                                                            <p className='mb-0'>Q{index + 1} {titleize(participant?.ParticipantRole)} {sentiment['Score']}</p>
                                                                        )
                                                                    })
                                                                    }
                                                                </div>
                                                                <div className='ms-auto d-flex flex-column'>
                                                                    <CallsBarGraph
                                                                        data={participantStats[participant?.ParticipantRole]?.quarterlySentiment?.map((sentiment, index) => {
                                                                            return sentiment.Score
                                                                        })}
                                                                        tooltip='Agent'
                                                                        color={AppConfiguration?.PRODUCT_FAV_COLOR}
                                                                    />
                                                                    <div className="heightBarGraph">
                                                                        --
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            :
                                                            <div className='d-flex align-items-end'>
                                                                <div>
                                                                    {participantStats[participant?.ParticipantRole]?.quarterlySentiment?.map((sentiment, index) => {
                                                                        return (
                                                                            <p className='mb-0'>Q{index + 1} {titleize(participant?.ParticipantRole)} {sentiment['Score']}</p>
                                                                        )
                                                                    })
                                                                    }
                                                                </div>
                                                                <div className='ms-auto d-flex flex-column'>
                                                                    <CallsBarGraph
                                                                        data={participantStats[participant.ParticipantRole]?.quarterlySentiment?.map((sentiment, index) => {
                                                                            return sentiment?.Score
                                                                        })}
                                                                        tooltip='Customer'
                                                                        color={CUSTOMER_BAR_GRAPH_COLOR}
                                                                    />
                                                                    <div className="heightBarGraph">
                                                                        --
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                )
                                            })
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </Row>
                </CardBody>
            </Card>
        </div>
    );
}

export default CallStats;